// A `<task-notification>` prompt Claude Code wakes an agent with draws as the server's
// task_notification "finished" line (task-notification-tool-call.ts), never as XML or a file path.
import type { AgentTimelineItem, ToolCallItem } from "./types.js";

const MARKER = "<task-notification>";
// Claude Code 2.1.29x puts this preamble before the block on a subagent's wake prompt.
const PREAMBLE = "[SYSTEM NOTIFICATION - NOT USER INPUT]";
const BLOCK = /<task-notification>([\s\S]*?)<\/task-notification>/gi;

function tag(block: string, ...names: string[]): string | undefined {
  for (const name of names) {
    const value = new RegExp(`<${name}>\\s*([\\s\\S]*?)\\s*</${name}>`, "i").exec(block)?.[1];
    if (value?.trim()) return value.trim();
  }
  return undefined;
}

/** A prompt the harness injected for a finished background task, not words a person typed. */
export function isTaskNotificationPrompt(item: { type?: unknown; text?: unknown }): boolean {
  if (item.type !== "user_message" || typeof item.text !== "string") return false;
  const text = item.text.trimStart();
  return text.startsWith(MARKER) || (text.startsWith(PREAMBLE) && text.includes(MARKER));
}

// A background task's notification, as a tool card or as the prompt that carries it: no proof of a turn.
export function isTaskNotificationItem(item: {
  type?: unknown;
  name?: unknown;
  text?: unknown;
}): boolean {
  return (
    (item.type === "tool_call" && item.name === "task_notification") ||
    isTaskNotificationPrompt(item)
  );
}

function rowStatus(statuses: string[]): Pick<ToolCallItem, "status" | "error"> {
  const lower = statuses.map((s) => s.toLowerCase());
  if (lower.some((s) => s === "failed" || s === "error")) {
    return { status: "failed", error: { message: "Background task failed" } };
  }
  const stopped = lower.length > 0 && lower.every((s) => s === "stopped" || s.startsWith("cancel"));
  return { status: stopped ? "canceled" : "completed", error: null }; // null: the wire's "no error"
}

/** Unchanged, a finished line for a task-notification prompt, or undefined when nothing is readable. */
export function asTaskNotificationRow(item: AgentTimelineItem): AgentTimelineItem | undefined {
  if (item.type !== "user_message" || !isTaskNotificationPrompt(item)) return item;
  const blocks = [...item.text.matchAll(BLOCK)].map((m) => m[1]);
  const summaries = blocks.flatMap((b) => tag(b, "summary") ?? []);
  const statuses = blocks.flatMap((b) => tag(b, "status") ?? []);
  const label =
    summaries.join(" · ") || (statuses[0] ? `Background task ${statuses[0].toLowerCase()}` : "");
  const ids = blocks.flatMap((b) => tag(b, "task-id", "tool-use-id", "tool_use_id") ?? []);
  const taskIds = blocks.flatMap((b) => tag(b, "task-id") ?? []);
  const key = (ids.join("_") || item.messageId || "").replace(/[^a-zA-Z0-9._:-]+/g, "_");
  if (!label || !key) return undefined;
  return {
    type: "tool_call",
    callId: `task_notification_${key}`,
    name: "task_notification",
    detail: { type: "plain_text", label },
    // Only a harness prompt's <task-id> names the task the provider's line carries.
    ...(blocks.length === 1 && taskIds.length === 1 && !item.clientMessageId
      ? { metadata: { taskId: taskIds[0] } }
      : {}),
    ...rowStatus(statuses),
    ...(item.ts ? { ts: item.ts } : {}),
  };
}

/** The provider's own finished line for the same task is already drawn: the prompt adds nothing. */
export function finishedLineDrawn(
  entries: readonly { kind: string; name?: string; detail?: unknown }[],
  row: AgentTimelineItem,
): boolean {
  if (row.type !== "tool_call" || row.detail.type !== "plain_text") return false;
  const label = row.detail.label;
  return entries.some(
    (e) =>
      e.kind === "tool_call" &&
      e.name === "task_notification" &&
      (e.detail as { label?: unknown } | undefined)?.label === label,
  );
}

export function finishedTaskIdOf(item: { name: string; metadata?: unknown }): string | undefined {
  if (item.name !== "task_notification") return undefined;
  const taskId = (item.metadata as { taskId?: unknown } | undefined)?.taskId;
  return typeof taskId === "string" && taskId ? taskId : undefined;
}

/** The card a tool row updates: its own call, else the finished line already drawn for its task. */
export function toolCardIndexOf(
  entries: readonly { kind: string; callId?: string; taskId?: string }[],
  item: { callId: string; name: string; metadata?: unknown },
): number {
  const own = entries.findIndex((e) => e.kind === "tool_call" && e.callId === item.callId);
  const taskId = finishedTaskIdOf(item);
  if (own >= 0 || !taskId) return own;
  return entries.findIndex((e) => e.kind === "tool_call" && e.taskId === taskId);
}
